import React, { forwardRef, RefObject, useEffect, useRef, useState } from 'react';
import {
  BrushBehavior as d3BrushBehavior,
  descending as d3descending,
  extent as d3extent,
  max as d3max,
  min as d3min,
  scaleLinear as d3scaleLinear,
  scaleSqrt as d3scaleSqrt,
  select as d3select,
} from 'd3';

import { Button, Flexbox, Text, useTheme, useMessageFormatter } from '@redsift/design-system';
import intlMessages from './intl';

import { AnyScale, DotDatum, JSONArray, LegendItemDatum, LegendVariant } from '../../types';
import { ScatterPlotLegendVariant, ScatterPlotProps, ScatterPlotVariant } from './types';
import { StyledScatterPlot } from './styles';
import { useZoom, useColor, useBrush } from '../../hooks';
import { Axis, AxisVariant } from '../Axis';
import { Legend } from '../Legend';
import { Dot } from '../Dot';
import { BASE_NUMBER_OF_TICKS, countBy, group, sizeToDimension } from './utils';
import { mdiChevronDown, mdiChevronRight, mdiMouse } from '@redsift/icons';
import { Popover } from '@redsift/popovers';
import { mergeLegends, getSortingMethod } from '../../utils';

interface RenderedScatterPlotProps extends Required<Pick<ScatterPlotProps, 'data'>>, Omit<ScatterPlotProps, 'data'> {}

export const RenderedScatterPlot = forwardRef<HTMLDivElement, RenderedScatterPlotProps>((props, ref) => {
  const {
    isBrushable,
    className,
    data: propsData,
    dotRole,
    disableAnimations,
    getDotAnchorProps,
    hideControlKeyPanel,
    id,
    isDotSelected,
    labelDecorator,
    legendDecorator,
    legendVariant,
    legendProps,
    margins: propsMargins,
    onBrush,
    onBrushEnd,
    onDotClick,
    size,
    sortingMethod,
    colorTheme,
    tooltipDecorator,
    tooltipVariant,
    xAxisVariant,
    xAxisPlacement,
    xAxisTickFormat,
    xAxisTickPadding,
    xAxisTickRotation,
    xAxisTickSize,
    xAxisTickValues,
    yAxisVariant,
    yAxisPlacement,
    yAxisTickFormat,
    yAxisTickPadding,
    yAxisTickRotation,
    yAxisTickSize,
    yAxisTickValues,
    variant,
    ...forwardedProps
  } = props;

  const cache = useRef<JSONArray>();
  const theme = useTheme();

  const format = useMessageFormatter(intlMessages);

  const data = propsData.filter(
    (datum) =>
      datum.value &&
      typeof datum.key[0] === 'number' &&
      !Number.isNaN(datum.key[0]) &&
      typeof datum.key[1] === 'number' &&
      !Number.isNaN(datum.key[1])
  );

  useEffect(() => {
    cache.current = data;
  });

  const chartDimensions = sizeToDimension(size!);
  const width = chartDimensions.width;
  const height = chartDimensions.height;
  const margins = {
    top: xAxisVariant === AxisVariant.default || xAxisVariant === AxisVariant.tickValue ? 24 : 8,
    left: yAxisVariant === AxisVariant.default || yAxisVariant === AxisVariant.tickValue ? 48 : 8,
    right: yAxisVariant === AxisVariant.default || yAxisVariant === AxisVariant.tickValue ? 48 : 8,
    bottom: xAxisVariant === AxisVariant.default || xAxisVariant === AxisVariant.tickValue ? 24 : 8,
    ...propsMargins,
  };
  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = width - margins.left - margins.right;
  const minSize = d3min([chartWidth, chartHeight]) as number;

  const extendedScaleX = (() => {
    const domain = d3extent(data, (d) => d.key[0]) as [number, number];
    const gap = domain[1] - domain[0];
    domain[0] -= gap * 0.1;
    domain[1] += gap * 0.1;
    return d3scaleLinear().domain(domain).range([0, chartWidth]).nice();
  })();
  const extendedScaleY = (() => {
    const domain = d3extent(data, (d) => d.key[1]) as [number, number];
    const gap = domain[1] - domain[0];
    domain[0] -= gap * 0.1;
    domain[1] += gap * 0.1;
    return d3scaleLinear().domain(domain).range([chartHeight, 0]).nice();
  })();

  const hasCategory = data[0] && data[0].key.length >= 3 && data[0].key[2] !== null && data[0].key[2] !== undefined;
  const countsByCategory = hasCategory ? countBy(data, sortingMethod!) : undefined;
  const colorScale = useColor({
    data: countsByCategory || data,
    colorTheme: colorTheme!,
    category: 'key',
    theme,
  });

  const svgRef = useRef<SVGSVGElement>();
  const brushRef = useRef<d3BrushBehavior<unknown>>();
  const svgBrushRef = useRef<SVGGElement>();
  const { scaleX, scaleY, transform } = useZoom({
    svgRef,
    scaleX: extendedScaleX,
    scaleY: extendedScaleY,
    size,
    onZoom: () => {
      if (brushRef.current && svgBrushRef.current) {
        brushRef.current.clear(d3select(svgBrushRef.current));

        onBrushEnd?.(null);
      }
    },
  });
  useBrush({
    brushRef,
    svgRef: svgBrushRef,
    extent: [
      [0, 0],
      [chartWidth, chartHeight],
    ],
    scaleX,
    scaleY,
    isBrushable,
    isGridded: variant === ScatterPlotVariant.gridded,
    onBrush,
    onBrushEnd,
  });

  const circles =
    variant === ScatterPlotVariant.gridded
      ? group(data, scaleX, scaleY).sort((a, b) => {
          if (a.y !== b.y) {
            return b.y - a.y;
          }
          if (a.x !== b.x) {
            return a.x - b.x;
          }
          return d3descending(a.points.length, b.points.length);
        })
      : data.map((d) => ({
          category: d.key[2] ?? 'default',
          x: d.key[0],
          y: d.key[1],
          data: {
            key: d.key,
            value: 1,
          },
          points: [d],
        }));
  const globalSize =
    variant === ScatterPlotVariant.gridded
      ? d3scaleSqrt()
          .domain([1, Math.max(d3max(circles, (d) => d.points.length)!, 1)])
          .range([3, minSize / BASE_NUMBER_OF_TICKS / 2])
      : undefined;
  const legendWidth = useRef<string>();
  if (countsByCategory && !legendWidth.current) {
    legendWidth.current = `${
      Math.max(...countsByCategory.map((d) => d.key.length + String(d.value).length)) * 8 + 32
    }px`;
  }

  const [isKeyPanelOpen, setIsKeyPanelOpen] = useState(false);

  const categories: string[] = [];
  // const pivotedTable = circles.reduce((acc, item) => {
  //   const { x, category, y } = item;
  //   if (!categories.includes(category)) {
  //     categories.push(category);
  //   }
  //   const formattedKey = xAxisTickFormat ? xAxisTickFormat(x as any) : x.toString();

  //   let entry = acc.find((e) => e.key === formattedKey);
  //   if (!entry) {
  //     entry = { key: formattedKey };
  //     acc.push(entry);
  //   }

  //   entry[category] = y;

  //   return acc;
  // }, [] as Array<Record<string, string | number | null | undefined> & { key: string }>);

  const { extraLegendItems, ...forwardedLegendProps } = legendProps || {};

  return (
    <StyledScatterPlot
      mode={typeof onDotClick === 'function' || typeof getDotAnchorProps === 'function' ? 'interactive' : 'static'}
      definition={
        hasCategory
          ? format('definition-with-categories', {
              categoryLength: categories.length,
              circleLength: circles.length,
            })
          : format('definition', { circleLength: circles.length })
      }
      xAxisDefinition={format('x-axis-definition', {
        numAxis: xAxisPlacement === 'both' ? 2 : 1,
        start: scaleX.domain()[0],
        end: scaleX.domain()[1],
      })}
      yAxisDefinition={format('y-axis-definition', {
        numAxis: yAxisPlacement === 'both' ? 2 : 1,
        start: scaleY.domain()[0],
        end: scaleY.domain()[1],
      })}
      {...forwardedProps}
      id={id}
      className={className}
      ref={ref as RefObject<HTMLDivElement>}
    >
      <Flexbox flexDirection="column" alignItems="center" gap="8">
        {!hideControlKeyPanel ? (
          <Flexbox width={width} position="absolute">
            <Flexbox
              flexDirection="column"
              alignItems="flex-end"
              position="absolute"
              top={`${margins.top + 16}px`}
              right={`${margins.right + 16}px`}
            >
              <Popover placement="bottom-end" isOpen={isKeyPanelOpen} onOpen={setIsKeyPanelOpen}>
                <Popover.Trigger>
                  <Button
                    variant="secondary"
                    leftIcon={mdiMouse}
                    rightIcon={isKeyPanelOpen ? mdiChevronDown : mdiChevronRight}
                    color="grey"
                    style={{
                      border: '1px solid var(--redsift-color-neutral-mid-grey)',
                      gap: '4px',
                    }}
                  />
                </Popover.Trigger>
                <Popover.Content>
                  <Flexbox flexDirection="column" alignItems="flex-start">
                    <Text margin="8px 16px 0" fontSize="13px">
                      Zoom - Mouse wheel
                    </Text>
                    <Text margin="0 16px" fontSize="13px">
                      Pan - Left click and drag
                    </Text>
                    <Text margin="0 16px 8px" fontSize="13px">
                      Select - Hold shift, left click and drag
                    </Text>
                  </Flexbox>
                </Popover.Content>
              </Popover>
            </Flexbox>
          </Flexbox>
        ) : null}
        <svg ref={svgRef as RefObject<SVGSVGElement>} width={width} height={height}>
          <g ref={svgBrushRef as RefObject<SVGGElement>} transform={`translate(${margins.left},${margins.top})`} />
          <g transform={`translate(${margins.left},${margins.top})`}>
            <g>
              {circles.map((circle, index) => {
                const scaleXDomain = scaleX.domain();
                const scaleYDomain = scaleY.domain();

                if (
                  circle.x! < scaleXDomain[0] ||
                  circle.x! > scaleXDomain[1] ||
                  circle.y! < scaleYDomain[0] ||
                  circle.y! > scaleYDomain[1]
                ) {
                  return null;
                }

                const to = {
                  ...circle,
                  data: {
                    ...circle.data,
                    percent: circle.category
                      ? circle.data.value / countsByCategory?.find(({ key }) => key === circle.category)?.value!
                      : undefined,
                  },
                  r:
                    variant === ScatterPlotVariant.gridded
                      ? globalSize!(circle.points.length) / (transform.k < 1 ? transform.k : 1)
                      : 4,
                };

                return (
                  <Dot
                    color={colorScale?.(circle.category!)}
                    data={to}
                    dataset={circles}
                    isSelected={Boolean(isDotSelected?.(to))}
                    key={`circle _${index}`}
                    labelDecorator={labelDecorator}
                    onClick={onDotClick}
                    role={dotRole}
                    scaleX={scaleX}
                    scaleY={scaleY}
                    tooltipDecorator={tooltipDecorator}
                    tooltipVariant={tooltipVariant}
                  />
                );
              })}
            </g>
          </g>
          <g transform={`translate(${margins.left},${margins.top})`}>
            {['bottom', 'both'].includes(xAxisPlacement!) ? (
              <Axis
                disableAnimations={disableAnimations}
                position="bottom"
                length={chartWidth}
                scale={scaleX as AnyScale}
                x={0}
                y={chartHeight}
                tickValues={xAxisTickValues ?? 8}
                tickPadding={xAxisTickPadding}
                tickRotation={xAxisTickRotation}
                tickSize={xAxisTickSize}
                tickFormat={xAxisTickFormat}
                variant={xAxisVariant}
              />
            ) : null}
            {['top', 'both'].includes(xAxisPlacement!) ? (
              <Axis
                disableAnimations={disableAnimations}
                position="top"
                length={chartWidth}
                scale={scaleX as AnyScale}
                x={0}
                y={0}
                tickValues={xAxisTickValues ?? 8}
                tickPadding={xAxisTickPadding}
                tickRotation={xAxisTickRotation}
                tickSize={xAxisTickSize}
                tickFormat={xAxisTickFormat}
                variant={xAxisVariant}
              />
            ) : null}
            {['left', 'both'].includes(yAxisPlacement!) ? (
              <Axis
                disableAnimations={disableAnimations}
                position="left"
                length={chartHeight}
                scale={scaleY as AnyScale}
                x={0}
                y={chartHeight}
                tickValues={yAxisTickValues ?? 8}
                tickPadding={yAxisTickPadding}
                tickRotation={yAxisTickRotation}
                tickSize={yAxisTickSize}
                tickFormat={yAxisTickFormat}
                variant={yAxisVariant}
              />
            ) : null}
            {['right', 'both'].includes(yAxisPlacement!) ? (
              <Axis
                disableAnimations={disableAnimations}
                position="right"
                length={chartHeight}
                scale={scaleY as AnyScale}
                x={chartWidth}
                y={chartHeight}
                tickValues={yAxisTickValues ?? 8}
                tickPadding={yAxisTickPadding}
                tickRotation={yAxisTickRotation}
                tickSize={yAxisTickSize}
                tickFormat={yAxisTickFormat}
                variant={yAxisVariant}
              />
            ) : null}
          </g>
        </svg>
      </Flexbox>
      {hasCategory && legendVariant !== ScatterPlotLegendVariant.none ? (
        <Legend
          data={(extraLegendItems
            ? mergeLegends(extraLegendItems, countsByCategory!).sort(getSortingMethod(sortingMethod!))
            : countsByCategory!
          ).map((d) => ({
            ...d,
            color: colorScale?.(d.key)!,
          }))}
          variant={
            legendVariant === ScatterPlotLegendVariant.externalLabelValue
              ? LegendVariant.value
              : legendVariant === ScatterPlotLegendVariant.externalLabelPercent
              ? LegendVariant.percent
              : legendVariant === ScatterPlotLegendVariant.externalLabel
              ? LegendVariant.label
              : LegendVariant.custom
          }
          width={legendWidth.current}
          legendDecorator={
            legendDecorator
              ? (datum: LegendItemDatum, props) => legendDecorator(datum as unknown as DotDatum, props)
              : undefined
          }
          {...forwardedLegendProps}
        />
      ) : null}
    </StyledScatterPlot>
  );
});
